import React, { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, User, Phone } from 'lucide-react';
import LegalFooter from '@/components/LegalFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Nom systematiquement en majuscules (ex: DUPONT)
const formatLastName = (value) => value.toLocaleUpperCase('fr-FR');

// Prenom avec seulement la premiere lettre en majuscule (ex: Jean)
const formatFirstName = (value) => {
  if (!value) return value;
  return value.charAt(0).toLocaleUpperCase('fr-FR') + value.slice(1).toLocaleLowerCase('fr-FR');
};

// Numero de telephone avec un espace toutes les deux chiffres (ex: 06 80 62 39 54)
const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  return digits.replace(/(\d{2})(?=\d)/g, '$1 ');
};

const Login = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotIdentifier('');
    setForgotSent(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotIdentifier.trim()) return;
    setForgotSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotIdentifier.trim() })
      });
      // Le serveur répond toujours de façon identique (compte existant ou non),
      // pour ne pas révéler quels emails sont enregistrés dans l'application.
      if (response.ok) {
        setForgotSent(true);
      } else {
        toast.error('Erreur lors de la demande, merci de réessayer');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setForgotSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // Une coupure passagère du serveur (redémarrage, surcharge, 502/503/504
        // renvoyé par le proxy) ne doit pas faire échouer la connexion : on
        // réessaie automatiquement avant d'afficher une erreur.
        let response;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            response = await fetch(`${BACKEND_URL}/api/auth/login`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                identifier: formData.identifier,
                password: formData.password
              })
            });
            if (![502, 503, 504].includes(response.status)) break;
          } catch (networkError) {
            if (attempt === 2) throw networkError;
          }
          await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
        }

        if (response.ok) {
          const data = await response.json();
          if (rememberMe) {
            localStorage.setItem('rememberMe', 'true');
          }
          toast.success('Connexion réussie');
          onLogin(data.access_token, data.user);
        } else {
          const error = await response.json().catch(() => ({}));
          toast.error(error.detail || (response.status >= 500
            ? 'Le serveur ne répond pas, merci de réessayer dans un instant'
            : 'Identifiants incorrects'));
        }
      } else {
        const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: `${formData.lastName} ${formData.firstName}`.trim(),
            email: formData.email,
            phone: formData.phone,
            password: formData.password
          })
        });

        if (response.ok) {
          toast.success('Compte créé ! Il sera activé après validation par un administrateur.', { duration: 5000 });
          setIsLogin(true);
          setFormData({ ...formData, identifier: formData.email });
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Erreur lors de l\'inscription');
        }
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#0a1628] via-[#1a365d] to-[#2563eb]">
      {/* Login Card */}
      <div className="w-full max-w-[420px]">
        <div 
          className="bg-white rounded-2xl overflow-hidden"
          style={{ 
            boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.35), 0 10px 30px -10px rgba(0, 0, 0, 0.2), 0 0 40px -5px rgba(59, 130, 246, 0.15)' 
          }}
        >
          
          {/* Header with Logo */}
          <div className="pt-8 pb-5 px-8 text-center">
            <img 
              src="/logo-action-securite.png" 
              alt="Action Sécurité" 
              className="h-24 mx-auto mb-5 rounded-lg"
              style={{ imageRendering: 'crisp-edges' }}
            />
            <h1 className="text-[#0f172a] text-xl font-semibold tracking-tight mb-1">
              Main courante électronique
            </h1>
            <p className="text-[#9ca3af] text-sm">
              Accès sécurisé à votre espace
            </p>
          </div>

          {/* Tabs */}
          <div className="px-8 mb-6">
            <div className="flex bg-[#f1f5f9] rounded-xl p-1.5">
              <button
                type="button"
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isLogin 
                    ? 'bg-white text-[#0f172a] shadow-sm' 
                    : 'text-[#64748b] hover:text-[#475569]'
                }`}
              >
                Connexion
              </button>
              <button
                type="button"
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  !isLogin 
                    ? 'bg-white text-[#0f172a] shadow-sm' 
                    : 'text-[#64748b] hover:text-[#475569]'
                }`}
              >
                Inscription
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 pb-8">
            <div className="space-y-4">
              {!isLogin && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-[#374151]">
                      Prénom
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <User className="h-[18px] w-[18px] text-[#9ca3af]" />
                      </div>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: formatFirstName(e.target.value) })}
                        placeholder="Jean"
                        className="w-full pl-11 pr-4 py-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:border-[#2563eb] focus:ring-3 focus:ring-[#2563eb]/15 transition-all duration-150"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-[#374151]">
                      Nom
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <User className="h-[18px] w-[18px] text-[#9ca3af]" />
                      </div>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: formatLastName(e.target.value) })}
                        placeholder="DUPONT"
                        className="w-full pl-11 pr-4 py-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:border-[#2563eb] focus:ring-3 focus:ring-[#2563eb]/15 transition-all duration-150"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-[#374151]">
                  {isLogin ? 'Email ou téléphone' : 'Email'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className="h-[18px] w-[18px] text-[#9ca3af]" />
                  </div>
                  <input
                    type={isLogin ? 'text' : 'email'}
                    value={isLogin ? formData.identifier : formData.email}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      [isLogin ? 'identifier' : 'email']: e.target.value 
                    })}
                    placeholder="nom@entreprise.com"
                    className="w-full pl-11 pr-4 py-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:border-[#2563eb] focus:ring-3 focus:ring-[#2563eb]/15 transition-all duration-150"
                    required
                  />
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-[#374151]">
                    Téléphone
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <Phone className="h-[18px] w-[18px] text-[#9ca3af]" />
                    </div>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                      placeholder="06 12 34 56 78"
                      className="w-full pl-11 pr-4 py-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:border-[#2563eb] focus:ring-3 focus:ring-[#2563eb]/15 transition-all duration-150"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-[#374151]">
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Lock className="h-[18px] w-[18px] text-[#9ca3af]" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full pl-11 pr-11 py-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:border-[#2563eb] focus:ring-3 focus:ring-[#2563eb]/15 transition-all duration-150"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-[#9ca3af] hover:text-[#64748b] transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                  </button>
                </div>
              </div>

              {isLogin && (
                <div className="flex items-center justify-between pt-2">
                  <label className="flex items-center gap-2.5 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-[#d1d5db] text-[#2563eb] focus:ring-[#2563eb] focus:ring-offset-0 cursor-pointer"
                    />
                    <span className="text-sm text-[#64748b] group-hover:text-[#475569] transition-colors">
                      Se souvenir de moi
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-[#2563eb] hover:text-[#1d4ed8] font-medium transition-colors"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-6 py-3 bg-[#2563eb] text-white text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed hover:bg-[#1d4ed8] hover:shadow-xl hover:shadow-blue-600/30 hover:-translate-y-0.5 active:translate-y-0 active:shadow-lg"
              style={{ boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.35)' }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Chargement...
                </span>
              ) : (
                isLogin ? 'Se connecter' : 'Créer mon compte'
              )}
            </button>

            <p className="text-center text-sm text-[#64748b] mt-6">
              {isLogin ? "Pas encore de compte ?" : "Déjà un compte ?"}{' '}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#2563eb] hover:text-[#1d4ed8] font-semibold transition-colors"
              >
                {isLogin ? "S'inscrire" : "Se connecter"}
              </button>
            </p>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-blue-200/50 mt-6">
          © 2025 Action Sécurité · Solution sécurisée
        </p>
        <LegalFooter className="text-blue-200/70 !mt-2" />
      </div>

      {/* Popup mot de passe oublié */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div
            className="w-full max-w-[400px] bg-white rounded-2xl overflow-hidden p-6"
            style={{ boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.35)' }}
          >
            {forgotSent ? (
              <div className="text-center space-y-3">
                <div className="w-12 h-12 rounded-full bg-[#dcfce7] flex items-center justify-center mx-auto">
                  <Mail className="h-6 w-6 text-[#16a34a]" />
                </div>
                <h2 className="text-[#0f172a] text-lg font-semibold">Email envoyé</h2>
                <p className="text-sm text-[#64748b]">
                  Si un compte existe avec cet email, un lien de réinitialisation vient d'être envoyé.
                  Pensez à vérifier vos spams.
                </p>
                <button
                  type="button"
                  onClick={closeForgotPassword}
                  className="w-full mt-2 py-2.5 bg-[#2563eb] text-white text-sm font-semibold rounded-xl hover:bg-[#1d4ed8] transition-colors"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <h2 className="text-[#0f172a] text-lg font-semibold mb-1">Mot de passe oublié</h2>
                  <p className="text-sm text-[#64748b]">
                    Indiquez votre email pour recevoir un lien de réinitialisation.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-[#374151]">Email</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <Mail className="h-[18px] w-[18px] text-[#9ca3af]" />
                    </div>
                    <input
                      type="email"
                      autoFocus
                      value={forgotIdentifier}
                      onChange={(e) => setForgotIdentifier(e.target.value)}
                      placeholder="nom@entreprise.com"
                      className="w-full pl-11 pr-4 py-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:border-[#2563eb] focus:ring-3 focus:ring-[#2563eb]/15 transition-all duration-150"
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeForgotPassword}
                    className="flex-1 py-2.5 bg-[#f1f5f9] text-[#475569] text-sm font-semibold rounded-xl hover:bg-[#e2e8f0] transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={forgotSubmitting}
                    className="flex-1 py-2.5 bg-[#2563eb] text-white text-sm font-semibold rounded-xl hover:bg-[#1d4ed8] transition-colors disabled:opacity-70"
                  >
                    {forgotSubmitting ? 'Envoi...' : 'Envoyer'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
